import React from "react";
import { Link } from "react-router-dom";
import { Trophy, ListOrdered, MessageSquare, IdCard, Shield, Hexagon, ChevronRight, ArrowUpRight, Sparkles } from "lucide-react";
import { LOGO_FULL, LOGO_MARK, WALLPAPER, TAGLINE } from "../lib/brand";
import { LogoMark, LogoFull } from "../components/Logo";

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <LogoMark src={LOGO_MARK} size="w-9 h-9" />
            <span className="brand-text text-xl tracking-tight hidden sm:inline">NORDGAMING</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="btn-ghost" data-testid="header-login-link">
              Login
            </Link>
            <Link to="/register" className="btn-primary" data-testid="header-register-link">
              Mitglied werden
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          {WALLPAPER && <img src={WALLPAPER} alt="" className="w-full h-full object-cover opacity-70" />}
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/85 to-bg/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-bg via-transparent to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full grid grid-cols-12 gap-8 pt-28 pb-16 relative">
          <div className="col-span-12 lg:col-span-7 space-y-8 fade-in">
            <div className="chip text-primary border-primary/40 bg-primary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Season 2026 · EA FC 26 · Nordic E-Sports
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              Das Portal für<br />
              <span className="text-gradient animate-gradient-shift bg-[length:200%_200%]">kompetitive</span><br />
              Nordgaming Athleten.
            </h1>
            <p className="text-white/70 text-lg max-w-xl leading-relaxed">
              Wettkämpfe, Ranglisten, Vereins-Chat und dein digitaler Sportlerausweis — alles an einem Ort.
              Gebaut für den modernen E-Sports Verein.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-register">
                Jetzt beitreten
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/login" className="btn-secondary" data-testid="hero-cta-login">
                Als Mitglied anmelden
              </Link>
            </div>

            <div className="pt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "Active", v: "120+", l: "Mitglieder" },
                { k: "Season", v: "08", l: "Turniere" },
                { k: "Games", v: "EA FC", l: "Liga aktiv" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-transparent pl-4" style={{ borderImage: "linear-gradient(180deg, #29E9FF, #C247FF) 1" }}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{s.k}</div>
                  <div className="font-display text-3xl font-bold tabular-nums mt-1 text-gradient">{s.v}</div>
                  <div className="text-xs text-white/60">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Logo floating */}
          <div className="hidden lg:flex col-span-5 items-center justify-center relative">
            <LogoFull src={LOGO_FULL} data-testid="hero-logo-full" />
          </div>

          <div className="col-span-12 lg:hidden flex items-center justify-center">
            <LogoMark src={LOGO_MARK} size="w-36 h-36" className="animate-float" />
          </div>
        </div>

        {/* Bottom marquee tagline */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-bg/80 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 md:px-8 h-12 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.3em]">
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="w-3 h-3" />
              <span className="brand-text">{TAGLINE}</span>
            </div>
            <div className="hidden md:block text-white/40">Scroll ↓ mehr entdecken</div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-28 border-t border-white/10 relative">
        <div className="absolute inset-0 hex-grid-bg opacity-30" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-16">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">01 · Module</div>
              <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tight">
                Alles was<br />dein Team <span className="text-gradient">braucht</span>.
              </h2>
            </div>
            <Link to="/register" className="link-underline text-white/70 hover:text-primary text-sm" data-testid="features-register-link">
              Zugang anfordern →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {[
              { icon: Trophy, title: "Wettkampfübersicht", desc: "Turniere, Brackets & Live-Scores — zentral verwaltet.", code: "01" },
              { icon: ListOrdered, title: "Ranglisten", desc: "Live-Leaderboards für jedes Game mit Win Rate & Streaks.", code: "02" },
              { icon: MessageSquare, title: "Vereins-Chat", desc: "Kanäle (#general, #ea-fc-26, #turniere) und Direktnachrichten.", code: "03" },
              { icon: IdCard, title: "Digitaler Sportlerausweis", desc: "Deine Mitgliedschaft als Karte — mit Apple & Google Wallet.", code: "04" },
              { icon: Shield, title: "Admin Panel", desc: "Turniere, Ranglisten & Mitglieder verwalten.", code: "05" },
              { icon: Hexagon, title: "KI-Ready", desc: "Match-Summaries, Auto-Moderation und intelligente Analyse — bereit für die Zukunft.", code: "06" },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-bg p-8 group hover:bg-surface transition-colors relative"
                data-testid={`feature-card-${i}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{f.code}</div>
                <div className="mt-10 mb-6 inline-flex items-center justify-center w-12 h-12 rounded-sm bg-neon-gradient-soft border border-white/10">
                  <f.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl tracking-tight mb-2 group-hover:text-gradient">{f.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{f.desc}</p>
                <ChevronRight className="absolute top-8 right-6 w-4 h-4 text-white/20 group-hover:text-primary transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 border-t border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          {WALLPAPER && <img src={WALLPAPER} alt="" className="w-full h-full object-cover opacity-20" />}
          <div className="absolute inset-0 bg-bg/70" />
        </div>
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <LogoMark src={LOGO_MARK} size="w-20 h-20" className="mx-auto mb-8 animate-float" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-4">02 · Join the club</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-tight">
            Bereit für die<br />nächste <span className="text-gradient">Season</span>?
          </h2>
          <p className="text-white/60 mt-6 max-w-xl mx-auto">
            Registriere dich in weniger als einer Minute und hol dir deinen digitalen Nordgaming Sportlerausweis.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/register" className="btn-primary" data-testid="cta-register">
              Jetzt registrieren
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="btn-secondary" data-testid="cta-login">
              Ich bin bereits Mitglied
            </Link>
          </div>
          <div className="mt-10 brand-text text-xl tracking-widest">{TAGLINE.toUpperCase()}</div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between text-xs text-white/40 font-mono uppercase tracking-[0.2em]">
          <div className="flex items-center gap-2">
            <LogoMark src={LOGO_MARK} size="w-5 h-5" />
            <span>© Nordgaming e.V. · 2026</span>
          </div>
          <div>Nordgaming Portal</div>
        </div>
      </footer>
    </div>
  );
}
